import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../Store/apiStore";
import decodeToken from "../lib/decodeToken";

/**
 * useSkipDetails — called from the sign-in redirect (SignUp / Onboarding / Start)
 * for users whose details are NOT verified yet. It runs the lightweight
 * background work, then sends the user to the Personal Details screen
 * (/details). That screen saves name/phone (verifyDetails: true, owner email)
 * and then forwards to /business-step via useDetailsNavigation.
 *
 *   1. ReactNative WebView location handshake (GET_LOCATION),
 *   2. UTM attribution sync (once, guarded by the utmSynced flag),
 *
 * The name `skipToBusinessStep` is kept so existing callers don't change.
 */
export default function useSkipDetails() {
  const navigate = useNavigate();

  const skipToBusinessStep = useCallback(
    async ({ replace = false, email, userId: passedUserId } = {}) => {
      const token = localStorage.getItem("token");
      const decoded = decodeToken(token);
      const userId = passedUserId || decoded?.id || decoded?.userId || localStorage.getItem("userId");

      // 1) ReactNative WebView location handshake (mobile app shell).
      try {
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({ type: "GET_LOCATION", ...(email ? { email } : {}) })
          );
        }
      } catch (_) {
        /* not in a WebView — ignore */
      }

      // 2) UTM attribution — once (Google/Apple sign-ups don't send it at login).
      if (userId) {
        try {
          const utmData = JSON.parse(localStorage.getItem("utm_data") || "null");
          if (
            utmData &&
            Object.keys(utmData).length > 0 &&
            localStorage.getItem("utmSynced") !== "true"
          ) {
            await axios.put(`${API_BASE_URL}/endusers/users/${userId}`, { utm_data: utmData });
            localStorage.setItem("utmSynced", "true");
          }
        } catch (err) {
          console.error("useSkipDetails: UTM sync failed:", err?.message);
        }
      }

      // Show the Personal Details screen before the business stepp.
      navigate("/details", { replace });
    },
    [navigate]
  );

  return { skipToBusinessStep };
}
